import NavigateBackWizardStep from "../../components/navigateBackWizardStep";
import { useWizard } from "react-use-wizard";
import React, { useCallback, useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useTranslation } from "react-i18next";
import createPersistedState from "use-persisted-state";

const useClinicState = createPersistedState("clinic");

function SelectDayStep({
  setCurrentDate,
  selectedClinic,
  recDate,
  setRecDate,
  recDoctor,
  isReschedule,
  availableClinics,
}) {
  const { previousStep, nextStep, goToStep } = useWizard();
  const [date, setDate] = useState(new Date());
  const { t } = useTranslation();
  const [clinicId, setClinic] = useClinicState();

  // Similar implementation to Receptionist module
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarEventsRaw, setCalendarEventsRaw] = useState([]);
  const [isFetchingData, setIsFetchingData] = useState(true);

  const selectedDate = (value) => {
    setDate(value);
    localStorage.setItem("Date", value);
    return nextStep();
  };

  const fromDate = `${date.getFullYear()}-${
    date.getMonth() + 1
  }-${date.getDate()}`;
  const patientId = JSON.parse(localStorage.getItem("pid"));
  // fetch URL
  const getURL = useCallback(
    (selectedClinic) =>
      `/api/v1/appointments/slots?days=60&spaceId=${selectedClinic}&ownerId=${
        recDoctor.drId
      }&fromDate=${fromDate}&attendeeId=${patientId}${
        isReschedule ? "&reschedule=true" : ""
      }`,
    [recDoctor.drId, fromDate, patientId, isReschedule]
  );

  useEffect(() => {
    const fetchData = async () => {
      setIsFetchingData(true);
      const token = "Bearer " + localStorage.getItem("token");
      try {
        if (selectedClinic !== "CC000000") {
          const response = await fetch(getURL(selectedClinic), {
            method: "GET",
            headers: {
              Accept: "application/json; charset=utf-8",
              Authorization: token.replace(/['"]+/g, ""),
            },
            credentials: "include",
          });
          const data = await response.json();
          //console.log(data.dates);

          // New State
          let dates = data.dates;
          if (dates) {
            let availableDates = dates.filter((date) => {
              return (
                date.available || (!date.available && date.slots.length > 0)
              );
            });
            let eventDates =
              availableDates
                ?.map((date) => {
                  return date.date;
                })
                .map((dateString) => {
                  return {
                    groupId: "available",
                    start: dateString,
                    end: dateString,
                    display: "inverse-background",
                  };
                }) || [];
            setCalendarEvents(eventDates);
            setCalendarEventsRaw(dates);
            setIsFetchingData(false);
          }

          if (response.status !== 200) {
            const error = new Error(response.error);
            throw error;
          }
        } else {
          const response = await Promise.all(
            availableClinics
              .filter((clinic) => clinic.online)
              .map((clinic) =>
                fetch(getURL(clinic.code), {
                  method: "GET",
                  headers: {
                    Accept: "application/json; charset=utf-8",
                    Authorization: token.replace(/['"]+/g, ""),
                  },
                  credentials: "include",
                })
                  .then((data) => data.json())
                  .then((clinicSlotsResponse) => {
                    if (clinicSlotsResponse.status.success) {
                      return {
                        ...clinicSlotsResponse,
                        dates: clinicSlotsResponse.dates.map((date) => {
                          return {
                            ...date,
                            clinicId: clinic.code,
                            slots: date.slots.map((slot) => {
                              return { ...slot, clinicId: clinic.code };
                            }),
                          };
                        }),
                      };
                    }
                    return clinicSlotsResponse;
                  })
              )
          );

          const combinedDateSlots = response.reduce((prev, current) => {
            const filteredDates = current.dates.filter(
              (date) => date.available
            );
            return [...prev, ...filteredDates];
          }, []);

          // Map to keep track of unique dates and their corresponding time slots
          var uniqueDates = new Map();

          // Populate the map with unique dates and their corresponding objects
          combinedDateSlots.forEach(function (dateObj) {
            if (!uniqueDates.has(dateObj.date)) {
              uniqueDates.set(dateObj.date, dateObj);
            } else {
              const existingObject = uniqueDates.get(dateObj.date);
              existingObject.timeSlots = [
                ...existingObject.timeSlots,
                ...dateObj.timeSlots,
              ];
              uniqueDates.set(dateObj.date, existingObject); // Update the map's value
            }
          });

          // Sort the filtered array based on the date property
          const sortedFilteredDateSlots = Array.from(uniqueDates.values()).sort(
            function (a, b) {
              return new Date(a.date) - new Date(b.date);
            }
          );

          console.log(sortedFilteredDateSlots);

          if (sortedFilteredDateSlots) {
            let availableDates = sortedFilteredDateSlots;
            let eventDates =
              availableDates
                ?.map((date) => {
                  return date.date;
                })
                .map((dateString) => {
                  return {
                    groupId: "available",
                    start: dateString,
                    end: dateString,
                    display: "inverse-background",
                  };
                }) || [];
            setCalendarEvents(eventDates);
            setCalendarEventsRaw(sortedFilteredDateSlots);
            setIsFetchingData(false);
          }
        }
      } catch (err) {
        console.log(err);
      }
    };
    fetchData();
  }, [getURL]);

  const handleDateClick = (arg) => {
    console.log(arg);
    let sd = calendarEventsRaw.find((event) => {
      return event.date === arg?.dateStr;
    });
    console.log(sd);
    if (sd && (sd.available || sd.bufferAvailable)) {
      setCurrentDate(sd);
      selectedDate(arg.date);
      setRecDate(undefined);
    } else if (sd && sd.available === false && sd.slots.length > 0) {
      setCurrentDate(sd);
      goToStep(5);
      setRecDate(undefined);
    }
  };

  function padTo2Digits(num) {
    return num.toString().padStart(2, "0");
  }

  function formatDate(date) {
    return [
      date.getFullYear(),
      padTo2Digits(date.getMonth() + 1),
      padTo2Digits(date.getDate()),
    ].join("-");
  }

  function getRawEvent(date) {
    return calendarEventsRaw.find((e) => e.date === date);
  }

  const dayCell = (arg) => {
    const _date = formatDate(arg.date);
    const _eventDetails = getRawEvent(_date);
    let _cssClass = "cd-slots-nostyle";
    if (_eventDetails) {
      if (_eventDetails.available) {
        _cssClass = "cd-slots-available";
      } else if (!_eventDetails.available && _eventDetails.slots.length > 0) {
        _cssClass = "cd-slots-full";
      }
    }
    let _html = `<div class="cd-slot-container"><div class="cd-slots-default ${_cssClass}">${arg.dayNumberText}</div></div>`;
    return { html: _html };
  };

  useEffect(() => {
    if (recDate != null && recDate != undefined) {
      handleDateClick({ date: new Date(recDate), dateStr: recDate });
      // setRecDate(undefined);
    }
  }, [calendarEventsRaw]);

  return (
    <>
      <NavigateBackWizardStep
        clickHandler={() =>
          selectedClinic === "CC000007" ? goToStep(0) : previousStep()
        }
        title={t("appointment.DATE.date")}
      />
      <div className="body_content p-0">
        <div className="inner_body">
          {isFetchingData ? (
            <div
              style={{
                margin: "auto",
                marginTop: "10em",
                display: "flex",
                justifyContent: "center",
              }}
              id="loader"
            ></div>
          ) : (
            <div>
              <div
                style={{ fontSize: "1.2em" }}
                className="text-center makeStyles-emptyStateText-5 mb-4 mt-2"
              >
                Please click on left and right arrow's <br /> to move between
                months.
              </div>
              <FullCalendar
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                fixedWeekCount={false}
                showNonCurrentDates={false}
                themeSystem={"bootstrap5"}
                eventBackgroundColor={"#dddddd"}
                selectable={true}
                nowIndicatorClassNames={"cd-slots-nostyle"}
                dayCellContent={dayCell}
                events={calendarEvents}
                height={420}
                nowIndicator={false}
                headerToolbar={{
                  left: "prev",
                  center: "title",
                  right: "next",
                }}
                initialDate={recDate}
                dateClick={handleDateClick}
                //initialDate={recDate}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default SelectDayStep;
